import React, { useState, useRef } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Col,
  Spinner,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Edit, Trash, MoreVertical } from "lucide-react";

const IncomeHeadList = () => {
  // Dummy data for income heads
  const [incomeHeads, setIncomeHeads] = useState([
    { id: 1, name: "Rent", description: "Income from property rent" },
    { id: 2, name: "Sales", description: "Income from product sales" },
    { id: 3, name: "Donation", description: "Income from donations" },
    { id: 4, name: "Fees", description: "Income from school fees" },
    { id: 5, name: "Grants", description: "Income from grants" },
    { id: 6, name: "Sponsorship", description: "Income from sponsorships" },
    { id: 7, name: "Interest", description: "Income from bank interest" },
    { id: 8, name: "Dividends", description: "Income from investments" },
    { id: 9, name: "Royalties", description: "Income from royalties" },
    { id: 10, name: "Commissions", description: "Income from commissions" },
  ]);

  const [totalRecords, setTotalRecords] = useState(incomeHeads.length);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(5);
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIncomeHead, setSelectedIncomeHead] = useState(null);
  const [incomeHeadToDelete, setIncomeHeadToDelete] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  const toast = useRef(null);

  // Toggle dropdown
  const toggleDropdown = (id) => {
    setDropdownOpen(dropdownOpen === id ? null : id);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (selectedIncomeHead) {
        // Update income head
        const updatedIncomeHeads = incomeHeads.map((head) =>
          head.id === selectedIncomeHead.id ? { ...head, ...formData } : head
        );
        setIncomeHeads(updatedIncomeHeads);
        toast.current.show({
          severity: "success",
          summary: "Updated",
          detail: "Income head updated successfully",
          life: 3000,
        });
      } else {
        // Add new income head
        const newIncomeHead = { id: incomeHeads.length + 1, ...formData };
        setIncomeHeads([...incomeHeads, newIncomeHead]);
        setTotalRecords(incomeHeads.length + 1);
        toast.current.show({
          severity: "success",
          summary: "Added",
          detail: "Income head added successfully",
          life: 3000,
        });
      }
      toggle();
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save income head",
        life: 3000,
      });
    }
    setLoading(false);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!incomeHeadToDelete) return;
    setLoading(true);
    try {
      const updatedIncomeHeads = incomeHeads.filter(
        (head) => head.id !== incomeHeadToDelete.id
      );
      setIncomeHeads(updatedIncomeHeads);
      setTotalRecords(updatedIncomeHeads.length);
      toast.current.show({
        severity: "success",
        summary: "Deleted",
        detail: "Income head deleted successfully",
        life: 3000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete income head",
        life: 3000,
      });
    }
    setIncomeHeadToDelete(null);
    setLoading(false);
    toggleDeleteModal();
  };

  // Toggle modal
  const toggle = () => {
    if (modal) {
      setSelectedIncomeHead(null);
      setFormData({
        name: "",
        description: "",
      });
    }
    setModal(!modal);
  };

  // Toggle delete modal
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  // Handle pagination
  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  // Breadcrumb items
  const breadcrumbItems = [
    { title: "Finance", link: "/" },
    { title: "Income Head List", link: "#" },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Income Head List" breadcrumbItems={breadcrumbItems} />
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <span>Income Head List</span>
              <Button color="primary" onClick={toggle}>
                <i className="mdi mdi-plus mr-1"></i> Add Income Head
              </Button>
            </CardHeader>
            <CardBody>
              <DataTable
                value={incomeHeads.slice(first, first + rows)}
                loading={loading}
                stripedRows
              >
                {/* SI No Column */}
                <Column
                  header="SI No"
                  body={(rowData, { rowIndex }) => first + rowIndex + 1}
                />
                <Column field="name" header="Income Head" sortable />
                <Column field="description" header="Description" sortable />
                <Column
                  header="Actions"
                  body={(rowData) => (
                    <Dropdown
                      isOpen={dropdownOpen === rowData.id}
                      toggle={() => toggleDropdown(rowData.id)}
                    >
                      <DropdownToggle tag="button" className="btn btn-light">
                        <MoreVertical size={20} />
                      </DropdownToggle>
                      <DropdownMenu>
                        <DropdownItem
                          onClick={() => {
                            setSelectedIncomeHead(rowData);
                            setFormData({
                              name: rowData.name,
                              description: rowData.description,
                            });
                            toggle();
                          }}
                        >
                          <Edit size={16} className="me-2" />
                          Edit
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => {
                            setIncomeHeadToDelete(rowData);
                            toggleDeleteModal();
                          }}
                          className="text-danger"
                        >
                          <Trash size={16} className="me-2" />
                          Delete
                        </DropdownItem>
                      </DropdownMenu>
                    </Dropdown>
                  )}
                />
              </DataTable>
              <Paginator
                first={first}
                rows={rows}
                totalRecords={totalRecords}
                onPageChange={onPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      {/* Add/Edit Income Head Modal */}
      <Modal isOpen={modal} toggle={toggle} centered>
        <Form onSubmit={handleSubmit}>
          <ModalHeader toggle={toggle}>
            {selectedIncomeHead ? "Edit Income Head" : "Add Income Head"}
          </ModalHeader>
          <ModalBody>
            <Row>
              <Col md={12} className="mb-3">
                <FormGroup>
                  <Label>Income Head</Label>
                  <Input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
              <Col md={12} className="mb-3">
                <FormGroup>
                  <Label>Description</Label>
                  <Input
                    type="textarea"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggle}>
              Cancel
            </Button>
            <Button color="primary" type="submit" disabled={loading}>
              {selectedIncomeHead ? "Updating" : "Adding"}
              {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal} centered>
        <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
        <ModalBody>Are you sure you want to delete this income head?</ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete} >
            Delete
            {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
          </Button>
        </ModalFooter>
      </Modal>

      <Toast ref={toast} />
    </React.Fragment>
  );
};

export default IncomeHeadList;